<template>
  <TopBar>
    <template #page-title>
      <TopBarPageTitle
        :is-loading="pageLoading"
        :page-title="pageTitle"
        :tooltip="tooltip"
        :tooltip-max-width="tooltipMaxWidth"
      >
        <template
          v-if="route.name === 'sso-setup' && lastSsoLoginDate"
          #post-title
        >
          <DpplTooltip
            tooltip-placement="right"
            :tooltip-offset="8"
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <v-icon
                color="success"
                :size="18"
                class="sso-success-icon"
                icon="$checkbox-marked-circle"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
              />
            </template>
            <template #tooltip-content>
              <span class="text-body-2">Successful login at {{ lastSsoLoginDate }}</span>
            </template>
          </DpplTooltip>
        </template>
      </TopBarPageTitle>
    </template>
  </TopBar>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import TopBar from '@/components/shared/TopBar.vue';
import TopBarPageTitle from '@/components/shared/TopBarPageTitle.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useSettingsConfig } from '@/stores/settings/config';
import { useAppPlatform } from '@/stores/app/platform';
import { useSettingsSsoSetup } from '@/stores/settings/ssoSetup';
import { useSettingsIntegrations } from '@/stores/settings/integrations';
import { useTooltips } from '@/stores/insights/tooltips';

// Route
const route = useRoute();

// Stores
const appLoadingStore = useAppLoading();
const { pageLoading } = storeToRefs(appLoadingStore);

const settingsConfigStore = useSettingsConfig();
const { config } = storeToRefs(settingsConfigStore);

const appPlatformStore = useAppPlatform();
const { platform } = storeToRefs(appPlatformStore);

const ssoSetupStore = useSettingsSsoSetup();
const { lastSsoLoginDate } = storeToRefs(ssoSetupStore);

const settingsIntegrationsStore = useSettingsIntegrations();
const {
  isSmartRecruiters,
  isLever,
  isWorkday,
  isGreenhouse,
  isSuccessFactors,
  supportEmail,
} = storeToRefs(settingsIntegrationsStore);

const tooltipStore = useTooltips();
const { tooltips } = storeToRefs(tooltipStore);
const { loadTooltips } = tooltipStore;

// Computed
const pageTitle = computed(() => ({
  'sso-setup': 'Organization Settings',
  'ats-integration': `Connect ${platform.value.atsName}`,
  'custom-fields': 'Organization Settings',
  'user-roles': 'Organization Settings',
  'acknowledgements-settings': 'Organization Settings',
  'ai-settings': 'Organization Settings',
  'job-post-settings': 'Organization Settings',
  'job-profile-settings': 'Organization Settings',
}[route.name] || route.meta.title));

const canSendIntegrationsSupportEmail = computed(
  () => isSmartRecruiters
    || isLever
    || isGreenhouse
    || isWorkday
    || isSuccessFactors,
);

const preambleKeys = {
  account: 'account-settings-preamble',
  team: 'team-settings-preamble',
  templates: 'templates-settings-preamble',
  'sso-setup': 'sso-setup-settings-preamble',
  notifications: 'notifications-settings-preamble',
  'custom-fields': 'custom-fields-settings-preamble',
  'user-roles': 'user-roles-settings-preamble',
  'ats-integration': 'integrations-settings-preamble',
  'acknowledgements-settings': 'acknowledgements-settings-preamble',
  'ai-settings': 'ai-settings-preamble',
  'job-post-settings': 'job-post-settings-preamble',
  'job-profile-settings': 'job-profile-settings-preamble',
  'style-guide': 'style-guide-settings-preamble',
};

const getRouteNameForPreambles = computed(() => preambleKeys[route.name]);

const tooltipMaxWidth = computed(() => (
  route.name === 'ats-integration' ? '300px' : null
));

const tooltip = computed(() => {
  const preambleKey = preambleKeys[route.name] || '';
  let { body_r: html } = tooltips?.value?.[preambleKey] || {};

  if (route.name === 'ats-integration' && canSendIntegrationsSupportEmail.value && html) {
    const subject = encodeURIComponent(`${config.value.atsName} integration question`);
    html += '<br><br>Questions? Email us at '
      + `<a href="mailto:${supportEmail.value}?subject=${subject}">${supportEmail.value}</a>`;
  }

  return { key: preambleKey, html: html || '', title: '' };
});

// Watchers
watch(
  getRouteNameForPreambles,
  (newValue) => {
    if (newValue) loadTooltips([newValue]);
  },
  { immediate: true },
);
</script>

<style scoped lang="less">
.sso-success-icon {
  margin-right: 8px;
  margin-top: 1px;
}
</style>
